/**
 * The order-entry table for one mealtime inside an expanded day card — the
 * panel stacks one per mealtime, each headed by its mealtime name.
 *
 * Rows = visible menu items (thumbnail + name + kind). Columns = the
 * school's order columns, each header showing the column
 * label + `N students` (current-term enrollment). Cells = a qty `Input` —
 * except under `placedView` (a placed day, not in Edit mode), where every
 * quantity cell reads as plain text instead of a disabled input.
 * Presentational: quantities come via `getQty`, edits out via `onSetQty`;
 * the panel wires those to the draft (new order), the placed order (summary),
 * or the edit buffer (editing a placed order).
 */
import { resolveMenuItemTitle } from '@ordo/constants/menu-item-title'
import { productTypeLabel } from '@ordo/constants/product-type'
import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { Input } from '@ordo/ui/input'
import { MenuServiceBadge } from '@ordo/ui/menu-service-badge'
import { Backpack, ChevronRight, Gift, Milk, UtensilsCrossed } from 'lucide-react'
import { Fragment, type ReactElement, useState } from 'react'

import type { ComponentKind } from '@/api/location-settings'
import type { LocationActiveMenusResponse } from '@/api/orders'
import { AssetImage } from '@/components/asset-image'
import { DietaryTagChips } from '@/components/orders/dietary-tag-chips'
import { keyDietaryTags } from '@/lib/dietary-tags'
import { fieldTripMealTitle } from '@/lib/family-orders/field-trip-meal'
import { componentKindDisplayLabel, condimentListLabel, splitKinds } from '@/lib/school-orders/breakdown'
import { isItemAllowedForColumn, type OrderColumn, WHOLE_SCHOOL_COLUMN_ID } from '@/lib/school-orders/columns'
import { type ExtraOrderRow, extraRowCaption } from '@/lib/school-orders/field-trip'
import { itemPriceLabel } from '@/lib/school-orders/money'

import { MenuItemDetailModal } from './menu-item-detail-modal'

export type ActiveMenuItem = LocationActiveMenusResponse['menusForLocation'][number]['items'][number] & {
  /**
   * The owning menu's grade-range tags — `orderableMenusFor` attaches
   * them so `isItemAllowedForColumn` can gate each age-group column to
   * its own menu. Optional: family surfaces synthesize items without it
   * (absent = ungated).
   */
  menuGradeRangeCodes?: string[]
}

/** Display name for a menu item (catalog display labels via the shared resolver). */
export function menuItemName(it: ActiveMenuItem & { menuAudienceTier?: string }): string {
  const name = resolveMenuItemTitle(it) ?? 'Item'
  // An item on an ELECTED (field trip) menu reads as the field trip meal.
  return it.menuAudienceTier === 'ELECTED' ? fieldTripMealTitle(name) : name
}

/** Thumbnail URL for a menu item, if any. */
export function menuItemImageUri(it: ActiveMenuItem): string | null {
  return it.bundle?.imageUri ?? it.component?.imageUri ?? null
}

/** Kind label with the per-unit price appended when the school opts in ("Meal • $6.99"). */
export function menuItemKindLabelWithPrice(it: ActiveMenuItem, showPrice: boolean): string {
  // Product type, never "Meal": a syrup cup in a meal's column is a Condiment.
  const kind = productTypeLabel(it)
  const price = showPrice ? itemPriceLabel(it) : null
  return price ? `${kind} • ${price}` : kind
}

/**
 * Component quantities — optional wiring for DECOMPOSABLE bundle rows
 * (meals ordered whole, shipped component-wise; OVS columns only).
 *
 * Eligible rows swap the kind label for an expandable summary digesting
 * what actually ships ("Shipping every component with each meal" /
 * "135 milk · 120 fruit/juice" — see `componentSummaryLabel`), and the
 * expanded editor takes REAL QUANTITIES per kind per order column. The
 * box IS the number that ships: no percentages, no derived caption to
 * disagree with it (Sheila 2026-07-30).
 *
 * A cell is DERIVED (dashed) until the school types in it, at which point
 * it holds; emptying it reverts to derived. An explicit `0` is still a real
 * stored value — "ship none of this" — it just READS as `—` like every
 * other empty quantity box on the sheet; the dashed styling is what tells a
 * derived cell apart from a typed one.
 */
/** Milk-inside-the-meal editing surface (see `derivedMilk`). */
export interface DayOrderDerivedMilk {
  /**
   * One row per milk SKU for this mealtime, in menu order — NOT one per menu
   * item. A campus running two menus at the same mealtime carries the same
   * carton on both; `id` is the SKU row key the callbacks below take.
   */
  rows: Array<{ id: string; name: string }>
  /** Current quantity for a (milk SKU row, column) cell. */
  getQty: (milkRowKey: string, columnId: string) => number
  onSetQty: (milkRowKey: string, columnId: string, qty: number) => void
  /** True while the cell is still the derived default (dashed styling). */
  isDefault: (milkRowKey: string, columnId: string) => boolean
  /**
   * Whether this column serves the SKU at all — false when none of the menus
   * behind it cover the column's grade band, and the cell renders a dash.
   */
  isAvailable: (milkRowKey: string, columnId: string) => boolean
  /**
   * Standalone hosting only (campuses with NO component-split editor —
   * Straight Serve): whether any milk cell holds the school's own number.
   * Gates the standalone Milk disclosure's "Reset milk" action; the OVS
   * in-breakdown block keeps riding "Reset components" instead.
   */
  hasTyped?: () => boolean
  /** Hand every milk cell back to the share-derived suggestion. */
  onReset?: () => void
  /**
   * The counts shown are FROZEN placed quantities, not a live derivation —
   * a placed day that is not being edited.
   *
   * Drives the caption. "Auto-filled from the meal counts above" over numbers
   * that cannot move is how a Sep 1 order reading 45 meals / 5 milks looked
   * like a display bug instead of what it was: the count the day was committed
   * with (Hillsborough, 2026-08-26). `isDefault` is already forced false in
   * this state, so the cells render as real numbers — the caption was the only
   * thing still claiming otherwise.
   */
  countsArePlaced?: boolean
}

export interface DayOrderSplit {
  /** Whether this item row is decomposable at all. */
  supports: (item: ActiveMenuItem) => boolean
  /** The quantity that ships for a (row, column, kind) cell. */
  getQty: (menuItemId: string, columnId: string, kind: ComponentKind) => number
  onSetQty: (item: ActiveMenuItem, column: OrderColumn, kind: ComponentKind, qty: number) => void
  /** Clear a typed cell — it reverts to tracking the meal count. */
  onClearQty: (item: ActiveMenuItem, column: OrderColumn, kind: ComponentKind) => void
  /** Whether the row ships anything other than one-of-each-per-meal. */
  isCustom: (menuItemId: string) => boolean
  /** True while the cell is still derived from the meal count (dashed/italic). */
  isDefault: (menuItemId: string, columnId: string, kind: ComponentKind) => boolean
  /** The collapsed trigger's quantity-worded summary for a row. */
  summaryFor: (item: ActiveMenuItem) => string
  /** Whether the row has any typed cell at all — gates "Reset components". */
  hasTyped: (menuItemId: string) => boolean
  /** Drop every typed component quantity on the row, across all columns. */
  onResetRow: (item: ActiveMenuItem) => void
  /**
   * Per-column service-style gate for mixed campuses: a Straight Serve
   * (SS) column renders its cells locked at the meal count — every
   * component ships whole for that column. Absent = every column orders
   * by component (legacy campus-level behavior).
   */
  isComponentColumn?: (columnId: string) => boolean
  /**
   * Nested milk varieties — present only when the school links milk SKUs
   * (BEVERAGE-category group preferences). Renders indented per-SKU rows
   * under the Milk (BEVERAGE) kind row. The SKU rows are the only
   * editable milk numbers; the BEVERAGE total above them is their sum.
   */
  milk?: DayOrderMilk
}

export interface DayOrderMilk {
  /** The school's linked milk SKUs, in preference order. */
  skus: Array<{ id: string; name: string }>
  /** The quantity of one milk variety for a (row, column) cell. */
  getQty: (menuItemId: string, columnId: string, componentId: string) => number
  onSetQty: (item: ActiveMenuItem, column: OrderColumn, componentId: string, qty: number) => void
  /** Clear a typed variety cell — it reverts to apportioning the milk total. */
  onClearQty: (item: ActiveMenuItem, column: OrderColumn, componentId: string) => void
  /** True while the variety counts are still apportioned (dashed/italic inputs). */
  isDefault: (menuItemId: string, columnId: string, componentId: string) => boolean
  /**
   * Placed-day edits don't round-trip per-variety counts — there is no
   * `OrderItem` carrier for them — so the editor says so rather than
   * presenting numbers that are silently discarded on save.
   */
  recalculatedOnSave?: boolean
}

/** One menu's items — rendered as a titled section when several menus serve the mealtime. */
export interface DayOrderGroup {
  menuId: string
  menuName: string
  items: ActiveMenuItem[]
}

export interface DayOrderTableProps {
  items: ActiveMenuItem[]
  /**
   * The service day (`YYYY-MM-DD`) this sheet is for — handed to the item
   * detail modal so it describes the item AS SERVED that day (PROD-1386).
   */
  serviceDate?: string | null
  /**
   * Items grouped by owning menu, in the org's menu display order. When more
   * than one menu serves the mealtime, each menu renders as its own titled
   * section; a single menu renders headerless (as before). Absent = no
   * grouping (family/legacy callers).
   */
  groups?: DayOrderGroup[]
  columns: OrderColumn[]
  getQty: (menuItemId: string, columnId: string) => number
  onSetQty: (item: ActiveMenuItem, column: OrderColumn, qty: number) => void
  readOnly?: boolean
  /**
   * Prefill: true when the cell's value is a still-untouched suggestion —
   * rendered muted/italic until the user edits.
   */
  isSuggested?: (menuItemId: string, columnId: string) => boolean
  /**
   * Customers-administered display toggle (`Location.showItemPricesOnOrderTable`):
   * render each item's per-unit price next to its kind label ("Meal • $6.99").
   * Off by default — prices stay reachable via the item detail modal.
   */
  showItemPrices?: boolean
  /**
   * Milk-is-part-of-the-meal: hoist the menus' system milk rows
   * (`lineupMilkOptionCode`) out of the item list and into each meal's
   * component breakdown, where the panel derives their quantities from meal
   * counts x the BEVERAGE rate x the milk shares (dashed until edited).
   */
  derivedMilkActive?: boolean
  /**
   * The menu's system milk rows + per-(row, column) quantity accessors.
   * With a `split` editor (OVS), rendered as a "Milk" block INSIDE each
   * meal's component breakdown; without one (Straight Serve), rendered as
   * ONE standalone "Milk" disclosure per mealtime, whose dropdown holds the
   * per-SKU counts. Either way milk is part of the meal — never its own
   * order row. Quantities default to meal counts × the BEVERAGE rate × the
   * milk shares and stay dashed until the school types over one.
   */
  derivedMilk?: DayOrderDerivedMilk
  /**
   * Free holiday treats served at this mealtime today. Display-only: one per
   * meal, derived by the kitchen from the meal totals — never an order row
   * and never a count the school types.
   */
  holidayTreats?: ReadonlyArray<{ name: string; imageUri: string | null; perMeal: number }>
  split?: DayOrderSplit
  /**
   * Placed order lines whose menu item is NOT on this mealtime's browse
   * grid (field-trip bagged lines — their menus are hidden from the
   * school projection — or an item since dropped from the menu). Rendered
   * as ordinary quantity rows so day edits/cancels round-trip them instead
   * of silently dropping them; only `fieldTrip` rows get the "Field trip"
   * badge.
   */
  extraRows?: ExtraOrderRow[]
  /** Quantity edits for extra rows (they have no `ActiveMenuItem`). */
  onSetExtraQty?: (row: ExtraOrderRow, column: OrderColumn, qty: number) => void
  /**
   * Chef pre-approved swap: the placed line's re-rendered display name when
   * the kitchen replaced this item's component (order-local ShipmentLine
   * rewrite). Must win over the live-menu name — the menu still carries the
   * original component. Null/absent = no swap.
   */
  substitutedNameFor?: (menuItemId: string) => string | null
  /**
   * Placed-day summary: the day has a placed order and is NOT in Edit mode.
   * Quantity cells render as plain text via `placedQtyDisplay` — the ordered
   * number in green, a muted `0` where nothing was ordered — instead of
   * disabled inputs. `—` stays reserved for cells that were never orderable
   * ("not available for this group"). Implies the cells are uneditable;
   * callers still pass `readOnly` for the non-placed closed states.
   */
  placedView?: boolean
  /**
   * Committed parent (FAMILY_DIRECT) demand for a (row, column) cell —
   * rendered as a tiny "+X parent orders" legend under the cell in EVERY
   * mode (hidden at 0 so quiet columns stay quiet): on an unordered day
   * it previews parent demand while the school decides what to order; on
   * a placed day it sits under the placed number. Absent = no legend
   * (older payloads / non-school callers).
   */
  parentOrderedQty?: (menuItemId: string, columnId: string) => number
  /**
   * The mealtime this table belongs to ("Breakfast", "Lunch", …). Shown as
   * the first column header on desktop (in place of the generic "Item") and
   * as a section heading on mobile — the panel stacks one table per mealtime,
   * so each table names its own mealtime. Absent = legacy "Item" header.
   */
  mealtimeLabel?: string
}

/**
 * The one glyph an empty quantity box shows, everywhere on the sheet: meal
 * cells, component-kind cells, milk-variety cells and the derived-milk
 * block. An EM DASH (U+2014) — the same character the read-only "no meals
 * in this column" cells render, so a mixed row lines up visually
 * (Sheila 2026-08-02).
 */
export const EMPTY_QTY_PLACEHOLDER = '—'

/**
 * What a quantity box DISPLAYS for a stored count.
 *
 * Zero (and any nonsense negative) renders as an empty box, which surfaces
 * the `—` placeholder. This is a DISPLAY rule only: it does not touch what
 * the cell stores or ships, and typing `0` still writes a real `0` through
 * `onChange` — see `QtyInput`.
 */
export function qtyInputValue(value: number): string {
  return value > 0 ? String(value) : ''
}

/**
 * What a quantity CELL shows under `placedView` (a placed day, not in Edit
 * mode). Ordered cells read as the plain number; a cell the school left
 * empty reads as `0`. Distinct from `qtyInputValue`: inputs hide zero behind
 * the `—` placeholder, the placed summary states it. The `—` dash remains
 * reserved for cells that were never orderable at all ("not available for
 * this group").
 */
export function placedQtyDisplay(value: number): { text: string; ordered: boolean } {
  const qty = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
  return { text: String(qty), ordered: qty > 0 }
}

/**
 * The "+X parent orders" legend under a placed-day quantity: committed
 * FAMILY_DIRECT demand for the same cell. Null at zero — unordered
 * columns stay quiet rather than reading "+0" everywhere. Pure.
 */
export function parentOrdersLegend(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) {
    return null
  }
  const qty = Math.floor(count)
  return `+${qty} parent ${qty === 1 ? 'order' : 'orders'}`
}

/** Tiny parent-demand legend under a placed-day cell; nothing at 0. */
function ParentOrdersLegend({ count, className }: { count: number; className?: string }): ReactElement | null {
  const label = parentOrdersLegend(count)
  if (!label) {
    return null
  }
  return (
    <span className={cn('whitespace-nowrap text-[10px] leading-none text-muted-foreground', className)}>{label}</span>
  )
}

/**
 * Plain-text quantity cell for the placed-day summary — the ordered number
 * in bold green, a muted `0` otherwise. Same box footprint as the
 * `QtyInput` it stands in for (call sites pass the h/w via `className`) so
 * the sheet's grid doesn't shift between placed view and Edit mode —
 * except LEFT-ALIGNED, flush with the column header and the "+X parent
 * orders" legend below (the trailing `px-0` overrides the input
 * footprint's `px-2` via twMerge).
 */
function PlacedQtyText({
  value,
  className,
  ariaLabel,
}: {
  value: number
  className?: string
  ariaLabel?: string
}): ReactElement {
  const { text, ordered } = placedQtyDisplay(value)
  return (
    <span
      aria-label={ariaLabel}
      className={cn(
        'flex items-center whitespace-nowrap tabular-nums',
        ordered ? 'font-bold text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground',
        className,
        'justify-start px-0',
      )}>
      {text}
    </span>
  )
}

/**
 * Shell alignment for the read-only stand-in cells (`—` dashes, milk
 * totals): centered inside the input footprint while the sheet is an
 * entry form, LEFT-aligned in the placed-day summary so the whole column
 * lines up with `PlacedQtyText` and the header.
 */
function qtyCellShellClass(placedView: boolean): string {
  return placedView ? 'flex items-center justify-start' : 'grid place-items-center'
}

/**
 * Accessible name for a quantity box — "<row> · <column>". Since every empty
 * box now reads `—`, the placeholder can no longer stand in as the input's
 * name, so each cell carries an explicit one.
 *
 * The lone whole-school column is deliberately anonymous everywhere else on
 * the sheet (no column header, no per-input label — PR #1418 review), so it
 * is left out of the name here too rather than reintroduced in an attribute.
 */
export function qtyCellLabel(rowName: string, col: OrderColumn): string {
  return col.columnId === WHOLE_SCHOOL_COLUMN_ID ? rowName : `${rowName} · ${col.label}`
}

/**
 * Qty `Input` shared by the desktop table cell and the mobile grade grid.
 *
 * Every empty-or-zero box reads as `—` (see `EMPTY_QTY_PLACEHOLDER`). That
 * is a RENDERING rule, not a storage one: `onClear` still distinguishes
 * emptying the box ("revert to tracking the meal count") from typing `0`
 * ("ship none of this"), and the two stay distinguishable on screen by the
 * dashed/italic `suggested` styling a still-derived cell carries. Without
 * `onClear` a school could not order zero of a milk variety at all — the
 * value would collapse into the same untouched state as a blank.
 *
 * While the box is FOCUSED it shows exactly what was typed, not the value
 * echoed back from state. Clearing a derived cell makes it re-derive
 * immediately (that is the point of `onClear`), so without this the emptied
 * box would instantly repaint the derived number and the next digit would
 * APPEND to it — a school clearing `200` and typing `5` would land on
 * `2005`, clamped straight back to 200, unable to enter a small number at
 * all. The typed text is released on blur, when state takes over again.
 */
export function QtyInput({
  value,
  onChange,
  onClear,
  readOnly,
  suggested = false,
  display = false,
  className,
  max,
  ariaLabel,
}: {
  value: number
  onChange: (qty: number) => void
  onClear?: () => void
  readOnly?: boolean
  suggested?: boolean
  /** Placed-day summary: render the value as plain text (`PlacedQtyText`) instead of an input. */
  display?: boolean
  className?: string
  max?: number
  ariaLabel?: string
}): ReactElement {
  // Unconditionally before the `display` branch — the same mounted cell
  // flips display → input when the user clicks Edit, and the hook count
  // must not change across that flip.
  const [typed, setTyped] = useState<string | null>(null)
  if (display) {
    return <PlacedQtyText value={value} className={className} ariaLabel={ariaLabel} />
  }
  const fromState = qtyInputValue(value)
  return (
    <Input
      type="number"
      inputMode="numeric"
      min={0}
      max={max}
      aria-label={ariaLabel}
      placeholder={EMPTY_QTY_PLACEHOLDER}
      disabled={readOnly}
      value={typed ?? fromState}
      onBlur={() => setTyped(null)}
      onChange={(e) => {
        const raw = e.target.value
        setTyped(raw)
        if (raw === '') {
          if (onClear) {
            onClear()
            return
          }
          onChange(0)
          return
        }
        const n = Number(raw)
        if (!Number.isFinite(n) || n <= 0) {
          onChange(0)
          return
        }
        onChange(max === undefined ? Math.floor(n) : Math.min(Math.floor(n), max))
      }}
      className={cn(className, suggested && 'border-dashed italic text-muted-foreground')}
    />
  )
}

/**
 * Read-only "Condiments: Ketchup ×2 · Ranch" sublabel for bundle rows that
 * have NO component-split editor (Straight Serve campuses, plus
 * ships-complete and single-kind bundles on component campuses) — those rows
 * are not expandable, so the condiment line inside the expanded split section
 * can never show (PROD-136 reopen). Rows WITH a split editor keep their
 * existing behavior: condiments render under the expanded CONDIMENT kind row.
 *
 * `className` lets call sites diverge on overflow only (desktop truncates,
 * mobile wraps); `title` always carries the full list for hover reveal.
 */
function CondimentsLine({
  bundle,
  className,
}: {
  bundle: ActiveMenuItem['bundle']
  className?: string
}): ReactElement | null {
  const condiments = bundle?.condiments
  if (!condiments || condiments.length === 0) {
    return null
  }
  const label = `Condiments: ${condimentListLabel(condiments)}`
  return (
    <span title={label} className={cn('text-[11px] text-muted-foreground/80', className)}>
      {label}
    </span>
  )
}

export function DayOrderTable({
  items,
  serviceDate,
  groups,
  columns,
  getQty,
  onSetQty,
  readOnly = false,
  isSuggested,
  showItemPrices = false,
  split,
  derivedMilkActive = false,
  derivedMilk,
  holidayTreats = [],
  extraRows,
  onSetExtraQty,
  substitutedNameFor,
  placedView = false,
  parentOrderedQty,
  mealtimeLabel,
}: DayOrderTableProps): ReactElement {
  const rowName = (it: ActiveMenuItem): string => substitutedNameFor?.(it.id) ?? menuItemName(it)
  // Menus act as display sections: title each menu's block only when the
  // mealtime is served by more than one menu (a lone menu stays headerless).
  const menuSections: Array<{ key: string; title: string | null; caption?: string; items: ActiveMenuItem[] }> =
    groups && groups.filter((g) => g.items.length > 0).length > 1
      ? groups.filter((g) => g.items.length > 0).map((g) => ({ key: g.menuId, title: g.menuName, items: g.items }))
      : [{ key: 'all', title: null, items }]
  // Milk is part of the meal, so it is NOT its own row: the menu's system milk
  // rows are edited inside each meal's component breakdown (the "Shipping
  // every component with each meal" disclosure), exactly like every other one.
  // Hand-placed à-la-carte milk (no lineupMilkOptionCode) is a real standalone
  // product and stays where its menu put it.
  const sections: Array<{ key: string; title: string | null; caption?: string; items: ActiveMenuItem[] }> =
    derivedMilkActive
      ? menuSections
          .map((sec) => ({ ...sec, items: sec.items.filter((it) => it.lineupMilkOptionCode == null) }))
          .filter((sec) => sec.items.length > 0)
      : menuSections
  // Mealtimes stack vertically in the panel's shared scroll region, so the
  // table no longer owns its scroll — the header just carries the divider.
  const headCell = 'bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]'
  // Clicking an item row opens its read-only detail modal.
  const [detailItemId, setDetailItemId] = useState<string | null>(null)
  const detailItem = detailItemId ? sections.flatMap((s) => s.items).find((i) => i.id === detailItemId) : null
  // Component-split editor disclosure state, per item row.
  const [expandedSplits, setExpandedSplits] = useState<ReadonlySet<string>>(new Set())
  const toggleSplit = (menuItemId: string) => {
    setExpandedSplits((prev) => {
      const next = new Set(prev)
      if (next.has(menuItemId)) {
        next.delete(menuItemId)
      } else {
        next.add(menuItemId)
      }
      return next
    })
  }
  const splitMeta = (it: ActiveMenuItem) => {
    if (!split?.supports(it) || !it.bundle) {
      return null
    }
    const kinds = splitKinds(it.bundle.parts)
    if (kinds.length <= 1) {
      return null
    }
    return {
      kinds,
      expanded: expandedSplits.has(it.id),
      // Self-explaining digest of what ships — only deviating kinds, with
      // the column named when only some columns deviate, e.g. "135 milk (6-8)".
      summary: split.summaryFor(it),
    }
  }
  // The milk total for a (row, column): the sum of its variety counts.
  // Read-only — the varieties are the editable numbers, so the total can
  // never disagree with them (the old model let shares sum to ≠100% and
  // warned about it; that state is now unrepresentable).
  const milkTotalFor = (milk: DayOrderMilk, menuItemId: string, columnId: string): number =>
    milk.skus.reduce((sum, sku) => sum + milk.getQty(menuItemId, columnId, sku.id), 0)
  // Standalone Milk disclosure — the ONE milk surface for every campus.
  // Milk is a MEALTIME aggregate (rows are one per SKU, not one per menu
  // item), so it renders once at mealtime level; repeating the same total
  // under each expanded bundle read as additive and doubled the count on
  // OVS campuses (PROD-1214: 50 breakfasts showing "60 milk" for a real 30).
  // Milk stays part of the meal — never an orderable item row — but the
  // school can open the dropdown and override the per-SKU counts by hand
  // ("non OVS schools should be able to configure milk in dropdown still,
  // so they can change counts of just milk manually" — Sheila, 2026-08-25;
  // OLHSA orders 350 Whole / 5 Lactose-Free, not the even split). Typed
  // cells hold (never silently re-derived); "Reset milk" hands every cell
  // back to the share-derived suggestion.
  const standaloneMilk = derivedMilkActive && derivedMilk && derivedMilk.rows.length > 0 ? derivedMilk : null
  // Straight Serve columns on a mixed campus ship every component whole, so
  // their milk cells stay read-only — the same lock the component cells get.
  const milkColumnLocked = (columnId: string): boolean =>
    split?.isComponentColumn ? !split.isComponentColumn(columnId) : false
  const [milkExpanded, setMilkExpanded] = useState(false)
  /** The column's milk total (sum over its SKU cells); null = column unserved. */
  const milkColumnTotal = (columnId: string): number | null => {
    if (!standaloneMilk) {
      return null
    }
    const served = standaloneMilk.rows.filter((row) => standaloneMilk.isAvailable(row.id, columnId))
    if (served.length === 0) {
      return null
    }
    return served.reduce((sum, row) => sum + standaloneMilk.getQty(row.id, columnId), 0)
  }
  // Collapsed digest for the disclosure trigger: per-SKU totals across the
  // columns — "350 Whole Milk · 5 1% Lactose Free Milk".
  const milkSummary = standaloneMilk
    ? standaloneMilk.rows
        .map((row) => {
          const total = columns.reduce(
            (sum, col) =>
              sum +
              (standaloneMilk.isAvailable(row.id, col.columnId) ? standaloneMilk.getQty(row.id, col.columnId) : 0),
            0,
          )
          return `${total} ${row.name}`
        })
        .join(' · ')
    : ''
  const showMilkReset = standaloneMilk !== null && !readOnly && !placedView && (standaloneMilk.hasTyped?.() ?? false)
  return (
    <>
      {/* Mobile: each item stacks its grade inputs in a max-3-per-row grid
          (label → Quantity input → "N ordered"); a 4th grade wraps to a new
          row. No tooltips on touch, so grade names are spelled out in full. */}
      <div className="lg:hidden">
        {mealtimeLabel ? (
          <h3 className="border-b px-4 py-3 text-sm font-semibold text-foreground">{mealtimeLabel}</h3>
        ) : null}
        <ul className="divide-y">
          {sections.map((section) => (
            <Fragment key={section.key}>
              {section.title ? (
                <li className="bg-muted/50 px-4 py-2 text-xs text-muted-foreground">
                  <span className="font-semibold uppercase tracking-wide">{section.title}</span>
                  {section.caption ? <span className="ml-2 normal-case">{section.caption}</span> : null}
                </li>
              ) : null}
              {section.items.map((it) => {
                const imageUri = menuItemImageUri(it)
                const meta = splitMeta(it)
                // Key dietary claims (Vegetarian, Halal, …) render inline so
                // staff needn't click into the detail modal to verify one.
                const keyTags = keyDietaryTags(it.dietaryTags)
                return (
                  <li key={it.id} className="px-4 py-4">
                    <button
                      type="button"
                      onClick={() => setDetailItemId(it.id)}
                      title="View item details"
                      className="group flex w-full min-w-0 items-center gap-2.5 text-left focus:outline-none focus-visible:outline-none">
                      <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                        <AssetImage
                          src={imageUri}
                          className="h-full w-full object-cover"
                          fallback={<UtensilsCrossed className="h-4 w-4 text-muted-foreground" />}
                        />
                      </span>
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex items-center gap-1.5 truncate text-base font-medium group-hover:underline">
                          {it.serviceBadge?.kind === 'field-trip'
                            ? rowName(it).replace(/^Field Trip Meal: /, '')
                            : rowName(it)}
                          {it.serviceBadge && <MenuServiceBadge {...it.serviceBadge} />}
                          {split && it.bundle?.shipsComplete ? (
                            <Badge variant="outline" className="shrink-0 text-[10px] font-medium">
                              Ships complete
                            </Badge>
                          ) : null}
                          {it.quantityCap != null ? (
                            <Badge variant="secondary" className="shrink-0 text-[10px] font-medium">
                              Max {it.quantityCap}/day
                            </Badge>
                          ) : null}
                        </span>
                        {meta ? null : (
                          <>
                            <span className="text-sm text-muted-foreground">
                              {menuItemKindLabelWithPrice(it, showItemPrices)}
                            </span>
                            {/* Non-expandable rows surface condiments inline — mobile wraps. */}
                            <CondimentsLine bundle={it.bundle} />
                          </>
                        )}
                        {keyTags.length > 0 ? (
                          <span className="flex flex-wrap items-center gap-1 pt-0.5">
                            <DietaryTagChips tags={keyTags} />
                          </span>
                        ) : null}
                      </div>
                    </button>
                    {meta ? (
                      <button
                        type="button"
                        onClick={() => toggleSplit(it.id)}
                        aria-expanded={meta.expanded}
                        title={meta.summary}
                        className="-mx-1.5 mt-1 flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
                        <ChevronRight
                          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', meta.expanded && 'rotate-90')}
                        />
                        <span className="truncate underline decoration-muted-foreground/60 decoration-dashed underline-offset-4">
                          {meta.summary}
                        </span>
                      </button>
                    ) : null}
                    <div className="mt-3 grid grid-cols-3 gap-x-3 gap-y-4">
                      {columns.map((col) => (
                        <div key={col.columnId} className="flex min-w-0 flex-col gap-1.5">
                          {/* The lone whole-school column needs no name over each input. */}
                          {col.columnId === WHOLE_SCHOOL_COLUMN_ID ? null : (
                            <span className="truncate text-sm font-medium text-foreground">{col.label}</span>
                          )}
                          {isItemAllowedForColumn(it, col) ? (
                            <>
                              <QtyInput
                                value={getQty(it.id, col.columnId)}
                                onChange={(n) => onSetQty(it, col, n)}
                                readOnly={readOnly}
                                display={placedView}
                                suggested={isSuggested?.(it.id, col.columnId) ?? false}
                                ariaLabel={qtyCellLabel(rowName(it), col)}
                                className="h-9 w-full text-sm"
                              />
                              {parentOrderedQty ? (
                                <ParentOrdersLegend count={parentOrderedQty(it.id, col.columnId)} />
                              ) : null}
                            </>
                          ) : (
                            <span
                              title="Not available for this group"
                              className={cn(qtyCellShellClass(placedView), 'h-9 w-full text-sm text-muted-foreground')}>
                              —
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {meta?.expanded && split ? (
                      <div className="mt-2 space-y-3 border-l-2 border-border pl-3">
                        {meta.kinds.map((kind) => (
                          <div key={kind}>
                            <span className="text-xs font-medium text-muted-foreground">
                              {componentKindDisplayLabel(kind)}
                              {kind === 'MAIN_ENTREE' ? ' · one per meal' : ''}
                            </span>
                            {/* Which condiments ship with each meal — read-only. */}
                            {kind === 'CONDIMENT' && (it.bundle?.condiments?.length ?? 0) > 0 ? (
                              <span className="block text-[11px] text-muted-foreground/80">
                                {condimentListLabel(it.bundle!.condiments!)}
                              </span>
                            ) : null}
                            <div className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-2">
                              {columns.map((col) => {
                                const mealQty = getQty(it.id, col.columnId)
                                const columnLocked = split.isComponentColumn
                                  ? !split.isComponentColumn(col.columnId)
                                  : false
                                const milkRow = kind === 'BEVERAGE' && split.milk
                                return (
                                  <div key={col.columnId} className="flex min-w-0 flex-col gap-1">
                                    <span className="truncate text-[11px] text-muted-foreground">{col.label}</span>
                                    {mealQty <= 0 ? (
                                      <span
                                        title="No meals ordered for this group"
                                        className={cn(
                                          qtyCellShellClass(placedView),
                                          'h-8 w-full text-sm text-muted-foreground',
                                        )}>
                                        —
                                      </span>
                                    ) : milkRow ? (
                                      // The varieties below are the editable numbers;
                                      // this total is their sum.
                                      placedView ? (
                                        <PlacedQtyText
                                          value={milkTotalFor(split.milk!, it.id, col.columnId)}
                                          className="h-8 w-full text-sm"
                                        />
                                      ) : (
                                        <span className="grid h-8 w-full place-items-center text-sm tabular-nums text-muted-foreground">
                                          {milkTotalFor(split.milk!, it.id, col.columnId)}
                                        </span>
                                      )
                                    ) : (
                                      <QtyInput
                                        value={split.getQty(it.id, col.columnId, kind)}
                                        onChange={(n) => split.onSetQty(it, col, kind, n)}
                                        onClear={() => split.onClearQty(it, col, kind)}
                                        readOnly={readOnly || columnLocked || kind === 'MAIN_ENTREE'}
                                        display={placedView}
                                        suggested={
                                          !columnLocked &&
                                          kind !== 'MAIN_ENTREE' &&
                                          split.isDefault(it.id, col.columnId, kind)
                                        }
                                        max={mealQty}
                                        ariaLabel={qtyCellLabel(componentKindDisplayLabel(kind), col)}
                                        className="h-8 w-full text-sm"
                                      />
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                            {/* Nested milk varieties — real counts per SKU. */}
                            {kind === 'BEVERAGE' && split.milk
                              ? (() => {
                                  const milk = split.milk
                                  return (
                                    <div className="mt-2 space-y-2 border-l-2 border-border/60 pl-3">
                                      {milk.skus.map((sku) => (
                                        <div key={sku.id}>
                                          <span className="text-[11px] font-medium text-muted-foreground">
                                            {sku.name}
                                          </span>
                                          <div className="mt-1 grid grid-cols-3 gap-x-3 gap-y-2">
                                            {columns.map((col) => {
                                              const mealQty = getQty(it.id, col.columnId)
                                              const columnLocked = split.isComponentColumn
                                                ? !split.isComponentColumn(col.columnId)
                                                : false
                                              return (
                                                <div key={col.columnId} className="flex min-w-0 flex-col gap-1">
                                                  <span className="truncate text-[11px] text-muted-foreground">
                                                    {col.label}
                                                  </span>
                                                  {mealQty <= 0 ? (
                                                    <span
                                                      className={cn(
                                                        qtyCellShellClass(placedView),
                                                        'h-8 w-full text-sm text-muted-foreground',
                                                      )}>
                                                      —
                                                    </span>
                                                  ) : (
                                                    <QtyInput
                                                      value={milk.getQty(it.id, col.columnId, sku.id)}
                                                      onChange={(n) => milk.onSetQty(it, col, sku.id, n)}
                                                      onClear={() => milk.onClearQty(it, col, sku.id)}
                                                      readOnly={readOnly || columnLocked}
                                                      display={placedView}
                                                      suggested={
                                                        !columnLocked && milk.isDefault(it.id, col.columnId, sku.id)
                                                      }
                                                      max={mealQty}
                                                      ariaLabel={qtyCellLabel(sku.name, col)}
                                                      className="h-8 w-full text-sm"
                                                    />
                                                  )}
                                                </div>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      ))}
                                      {milk.recalculatedOnSave ? (
                                        <p className="text-[11px] text-muted-foreground">
                                          Per-variety counts are recalculated when you save.
                                        </p>
                                      ) : null}
                                    </div>
                                  )
                                })()
                              : null}
                          </div>
                        ))}
                        {/* System milk renders once at MEALTIME level (the
                            Milk disclosure below the items), never per
                            bundle — see PROD-1214. */}
                        {/* Undo every typed component number on the row. */}
                        {!readOnly && split.hasTyped(it.id) ? (
                          <button
                            type="button"
                            onClick={() => split.onResetRow(it)}
                            className="-mx-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 underline decoration-muted-foreground/60 decoration-dashed underline-offset-4 hover:bg-muted hover:text-foreground">
                            Reset components
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </Fragment>
          ))}
          {/* Free holiday treat — rides every meal at this mealtime today.
              Display-only: the kitchen packs one per meal off the meal totals,
              so there is nothing for the school to count or order. */}
          {holidayTreats.map((treat) => (
            <li key={treat.name} className="px-4 py-4">
              <div className="flex w-full min-w-0 items-center gap-2.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-emerald-50 dark:bg-emerald-950/40">
                  <Gift className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-base font-medium">Free {treat.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {treat.perMeal > 1 ? `${treat.perMeal} per meal` : 'One per meal'} — free, packed by the kitchen
                    from the meal totals
                  </span>
                </div>
              </div>
            </li>
          ))}
          {/* Milk is part of the meal — one mealtime-level disclosure for
              every campus (PROD-1214); the dropdown is where a school
              changes counts of just milk manually. */}
          {standaloneMilk ? (
            <li className="px-4 py-4">
              <div className="flex w-full min-w-0 items-center gap-2.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                  <Milk className="h-4 w-4 text-muted-foreground" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-base font-medium">Milk</span>
                  <span className="text-sm text-muted-foreground">
                    Included with each meal — auto-filled from the meal counts
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMilkExpanded((v) => !v)}
                aria-expanded={milkExpanded}
                title={milkSummary}
                className="-mx-1.5 mt-1 flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
                <ChevronRight
                  className={cn('h-3.5 w-3.5 shrink-0 transition-transform', milkExpanded && 'rotate-90')}
                />
                <span className="truncate underline decoration-muted-foreground/60 decoration-dashed underline-offset-4">
                  {milkSummary}
                </span>
              </button>
              <div className="mt-3 grid grid-cols-3 gap-x-3 gap-y-4">
                {columns.map((col) => {
                  const total = milkColumnTotal(col.columnId)
                  return (
                    <div key={col.columnId} className="flex min-w-0 flex-col gap-1.5">
                      {col.columnId === WHOLE_SCHOOL_COLUMN_ID ? null : (
                        <span className="truncate text-sm font-medium text-foreground">{col.label}</span>
                      )}
                      {total === null ? (
                        <span
                          title="Not available for this group"
                          className={cn(qtyCellShellClass(placedView), 'h-9 w-full text-sm text-muted-foreground')}>
                          —
                        </span>
                      ) : placedView ? (
                        <PlacedQtyText value={total} className="h-9 w-full text-sm" />
                      ) : (
                        // Read-only: the variety cells in the dropdown are the
                        // editable numbers and this total is their sum.
                        <span className="grid h-9 w-full place-items-center text-sm tabular-nums text-muted-foreground">
                          {total}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
              {milkExpanded ? (
                <div className="mt-2 space-y-3 border-l-2 border-border pl-3">
                  {standaloneMilk.rows.map((milkRow) => (
                    <div key={milkRow.id}>
                      <span className="text-[11px] text-muted-foreground">{milkRow.name}</span>
                      <div className="mt-1 grid grid-cols-3 gap-x-3 gap-y-2">
                        {columns.map((col) => {
                          const columnLocked = milkColumnLocked(col.columnId)
                          return (
                            <div key={col.columnId} className="flex min-w-0 flex-col gap-1">
                              <span className="truncate text-[11px] text-muted-foreground">{col.label}</span>
                              {standaloneMilk.isAvailable(milkRow.id, col.columnId) ? (
                                <QtyInput
                                  value={standaloneMilk.getQty(milkRow.id, col.columnId)}
                                  onChange={(n) => standaloneMilk.onSetQty(milkRow.id, col.columnId, n)}
                                  readOnly={readOnly || columnLocked}
                                  display={placedView}
                                  suggested={!columnLocked && standaloneMilk.isDefault(milkRow.id, col.columnId)}
                                  ariaLabel={qtyCellLabel(milkRow.name, col)}
                                  className="h-8 w-full text-sm"
                                />
                              ) : (
                                <span
                                  className={cn(
                                    qtyCellShellClass(placedView),
                                    'h-8 w-full text-sm text-muted-foreground',
                                  )}>
                                  {EMPTY_QTY_PLACEHOLDER}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {showMilkReset ? (
                    <button
                      type="button"
                      onClick={() => standaloneMilk.onReset?.()}
                      className="-mx-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 underline decoration-muted-foreground/60 decoration-dashed underline-offset-4 hover:bg-muted hover:text-foreground">
                      Reset milk
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ) : null}
          {(extraRows ?? []).map((row) => (
            <li key={`extra-${row.menuItemId}`} className="px-4 py-4">
              <div className="flex w-full min-w-0 items-center gap-2.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                  {row.fieldTrip ? (
                    <Backpack className="h-4 w-4 text-muted-foreground" />
                  ) : row.systemMilk ? (
                    <Milk className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                  )}
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex items-center gap-1.5 truncate text-base font-medium">
                    {row.name}
                    {row.fieldTrip ? (
                      <Badge variant="secondary" className="shrink-0 text-[10px] font-medium">
                        Field trip
                      </Badge>
                    ) : null}
                  </span>
                  <span className="text-sm text-muted-foreground">{extraRowCaption(row)}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-x-3 gap-y-4">
                {columns.map((col) => (
                  <div key={col.columnId} className="flex min-w-0 flex-col gap-1.5">
                    {/* The lone whole-school column needs no name over each input. */}
                    {col.columnId === WHOLE_SCHOOL_COLUMN_ID ? null : (
                      <span className="truncate text-sm font-medium text-foreground">{col.label}</span>
                    )}
                    <QtyInput
                      value={getQty(row.menuItemId, col.columnId)}
                      onChange={(n) => onSetExtraQty?.(row, col, n)}
                      readOnly={readOnly || !onSetExtraQty}
                      display={placedView}
                      ariaLabel={qtyCellLabel(row.name, col)}
                      className="h-9 w-full text-sm"
                    />
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Desktop: a horizontally-scrollable table, columns = grades. */}
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th
                className={cn('w-full px-4 py-3 text-left align-bottom text-sm font-medium text-foreground', headCell)}>
                {mealtimeLabel ?? 'Item'}
              </th>
              {columns.map((col) => (
                <th key={col.columnId} className={cn('whitespace-nowrap px-4 py-3 text-left align-bottom', headCell)}>
                  <div className="flex flex-col gap-0.5">
                    {/* The lone whole-school column needs no name over the Qty column
                        (Hunter, PR #1418 review) — the student count stays. */}
                    {col.columnId === WHOLE_SCHOOL_COLUMN_ID ? null : (
                      <span className="text-sm font-medium text-foreground">{col.label}</span>
                    )}
                    {col.sublabel ? (
                      <span className="text-xs font-normal text-muted-foreground">{col.sublabel}</span>
                    ) : null}
                    {col.studentCount !== null ? (
                      <span className="text-xs font-normal text-muted-foreground">{col.studentCount} students</span>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <Fragment key={section.key}>
                {section.title ? (
                  <tr className="border-b bg-muted/50">
                    <td colSpan={columns.length + 1} className="px-4 py-2 text-xs text-muted-foreground">
                      <span className="font-semibold uppercase tracking-wide">{section.title}</span>
                      {section.caption ? <span className="ml-2">{section.caption}</span> : null}
                    </td>
                  </tr>
                ) : null}
                {section.items.map((it) => {
                  const imageUri = menuItemImageUri(it)
                  const meta = splitMeta(it)
                  // Key dietary claims (Vegetarian, Halal, …) render inline so
                  // staff needn't click into the detail modal to verify one.
                  const keyTags = keyDietaryTags(it.dietaryTags)
                  return (
                    <Fragment key={it.id}>
                      <tr className="border-b last:border-b-0">
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => setDetailItemId(it.id)}
                            title="View item details"
                            className="group flex min-w-0 items-center gap-2.5 text-left focus:outline-none focus-visible:outline-none">
                            <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                              <AssetImage
                                src={imageUri}
                                className="h-full w-full object-cover"
                                fallback={<UtensilsCrossed className="h-4 w-4 text-muted-foreground" />}
                              />
                            </span>
                            <div className="flex min-w-0 flex-col gap-0.5">
                              <span className="flex items-center gap-1.5 truncate text-sm font-medium group-hover:underline">
                                {it.serviceBadge?.kind === 'field-trip'
                                  ? rowName(it).replace(/^Field Trip Meal: /, '')
                                  : rowName(it)}
                                {it.serviceBadge && <MenuServiceBadge {...it.serviceBadge} />}
                                {split && it.bundle?.shipsComplete ? (
                                  <Badge variant="outline" className="shrink-0 text-[10px] font-medium">
                                    Ships complete
                                  </Badge>
                                ) : null}
                                {it.quantityCap != null ? (
                                  <Badge variant="secondary" className="shrink-0 text-[10px] font-medium">
                                    Max {it.quantityCap}/day
                                  </Badge>
                                ) : null}
                              </span>
                              {/* Decomposable rows swap the kind label for the split summary toggle. */}
                              {meta ? null : (
                                <>
                                  <span className="text-xs text-muted-foreground">
                                    {menuItemKindLabelWithPrice(it, showItemPrices)}
                                  </span>
                                  {/* Non-expandable rows surface condiments inline — desktop truncates. */}
                                  <CondimentsLine bundle={it.bundle} className="truncate" />
                                </>
                              )}
                              {keyTags.length > 0 ? (
                                <span className="flex flex-wrap items-center gap-1 pt-0.5">
                                  <DietaryTagChips tags={keyTags} />
                                </span>
                              ) : null}
                            </div>
                          </button>
                          {meta ? (
                            <button
                              type="button"
                              onClick={() => toggleSplit(it.id)}
                              aria-expanded={meta.expanded}
                              title={meta.summary}
                              className="ml-[40px] mt-0.5 flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
                              <ChevronRight
                                className={cn('h-3 w-3 shrink-0 transition-transform', meta.expanded && 'rotate-90')}
                              />
                              <span className="truncate underline decoration-muted-foreground/60 decoration-dashed underline-offset-4">
                                {meta.summary}
                              </span>
                            </button>
                          ) : null}
                        </td>
                        {columns.map((col) => (
                          <td key={col.columnId} className="whitespace-nowrap px-4 py-3 align-top">
                            {isItemAllowedForColumn(it, col) ? (
                              <div className="flex flex-col items-start gap-1">
                                <QtyInput
                                  value={getQty(it.id, col.columnId)}
                                  onChange={(n) => onSetQty(it, col, n)}
                                  readOnly={readOnly}
                                  display={placedView}
                                  suggested={isSuggested?.(it.id, col.columnId) ?? false}
                                  ariaLabel={qtyCellLabel(rowName(it), col)}
                                  className="h-7 w-16 px-2 py-0 text-sm"
                                />
                                {parentOrderedQty ? (
                                  <ParentOrdersLegend count={parentOrderedQty(it.id, col.columnId)} />
                                ) : null}
                              </div>
                            ) : (
                              <span
                                title="Not available for this group"
                                className={cn(qtyCellShellClass(placedView), 'h-7 w-16 text-sm text-muted-foreground')}>
                                —
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                      {meta?.expanded && split
                        ? meta.kinds.map((kind) => (
                            <Fragment key={`${it.id}:${kind}`}>
                              <tr className="border-b bg-muted/30 last:border-b-0">
                                <td className="py-2 pl-[62px] pr-4 align-middle">
                                  <span className="text-xs text-muted-foreground">
                                    {componentKindDisplayLabel(kind)}
                                    {kind === 'MAIN_ENTREE' ? ' · one per meal' : ''}
                                  </span>
                                  {/* Which condiments ship with each meal — read-only. */}
                                  {kind === 'CONDIMENT' && (it.bundle?.condiments?.length ?? 0) > 0 ? (
                                    <span className="block text-[11px] text-muted-foreground/80">
                                      {condimentListLabel(it.bundle!.condiments!)}
                                    </span>
                                  ) : null}
                                </td>
                                {columns.map((col) => {
                                  const mealQty = getQty(it.id, col.columnId)
                                  const columnLocked = split.isComponentColumn
                                    ? !split.isComponentColumn(col.columnId)
                                    : false
                                  const isMilkTotal = kind === 'BEVERAGE' && split.milk
                                  return (
                                    <td key={col.columnId} className="whitespace-nowrap px-4 py-2 align-middle">
                                      {mealQty <= 0 ? (
                                        <span
                                          title="No meals ordered for this group"
                                          className={cn(
                                            qtyCellShellClass(placedView),
                                            'h-7 w-16 text-sm text-muted-foreground',
                                          )}>
                                          —
                                        </span>
                                      ) : isMilkTotal ? (
                                        // Read-only: the variety rows below are the
                                        // editable numbers and this is their sum.
                                        placedView ? (
                                          <PlacedQtyText
                                            value={milkTotalFor(split.milk!, it.id, col.columnId)}
                                            className="h-7 w-16 text-sm"
                                          />
                                        ) : (
                                          <span className="grid h-7 w-16 place-items-center text-sm tabular-nums text-muted-foreground">
                                            {milkTotalFor(split.milk!, it.id, col.columnId)}
                                          </span>
                                        )
                                      ) : (
                                        <QtyInput
                                          value={split.getQty(it.id, col.columnId, kind)}
                                          onChange={(n) => split.onSetQty(it, col, kind, n)}
                                          onClear={() => split.onClearQty(it, col, kind)}
                                          readOnly={readOnly || columnLocked || kind === 'MAIN_ENTREE'}
                                          display={placedView}
                                          suggested={
                                            !columnLocked &&
                                            kind !== 'MAIN_ENTREE' &&
                                            split.isDefault(it.id, col.columnId, kind)
                                          }
                                          max={mealQty}
                                          ariaLabel={qtyCellLabel(componentKindDisplayLabel(kind), col)}
                                          className="h-7 w-16 px-2 py-0 text-sm"
                                        />
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                              {/* Nested milk varieties — one indented row per
                              linked milk SKU, each a real COUNT. These are the
                              editable milk numbers; the BEVERAGE row above is
                              their sum. */}
                              {kind === 'BEVERAGE' && split.milk
                                ? (() => {
                                    const milk = split.milk
                                    return (
                                      <>
                                        {milk.skus.map((sku) => (
                                          <tr
                                            key={`${it.id}:${kind}:${sku.id}`}
                                            className="border-b bg-muted/30 last:border-b-0">
                                            <td className="py-2 pl-[78px] pr-4 align-middle">
                                              <span className="text-xs text-muted-foreground">{sku.name}</span>
                                            </td>
                                            {columns.map((col) => {
                                              const mealQty = getQty(it.id, col.columnId)
                                              const columnLocked = split.isComponentColumn
                                                ? !split.isComponentColumn(col.columnId)
                                                : false
                                              return (
                                                <td
                                                  key={col.columnId}
                                                  className="whitespace-nowrap px-4 py-2 align-middle">
                                                  {mealQty <= 0 ? (
                                                    <span
                                                      className={cn(
                                                        qtyCellShellClass(placedView),
                                                        'h-7 w-16 text-sm text-muted-foreground',
                                                      )}>
                                                      —
                                                    </span>
                                                  ) : (
                                                    <QtyInput
                                                      value={milk.getQty(it.id, col.columnId, sku.id)}
                                                      onChange={(n) => milk.onSetQty(it, col, sku.id, n)}
                                                      onClear={() => milk.onClearQty(it, col, sku.id)}
                                                      readOnly={readOnly || columnLocked}
                                                      display={placedView}
                                                      suggested={
                                                        !columnLocked && milk.isDefault(it.id, col.columnId, sku.id)
                                                      }
                                                      max={mealQty}
                                                      ariaLabel={qtyCellLabel(sku.name, col)}
                                                      className="h-7 w-16 px-2 py-0 text-sm"
                                                    />
                                                  )}
                                                </td>
                                              )
                                            })}
                                          </tr>
                                        ))}
                                        {milk.recalculatedOnSave ? (
                                          <tr className="border-b bg-muted/30 last:border-b-0">
                                            <td
                                              colSpan={columns.length + 1}
                                              className="py-1.5 pl-[78px] pr-4 text-[11px] text-muted-foreground">
                                              Per-variety counts are recalculated when you save.
                                            </td>
                                          </tr>
                                        ) : null}
                                      </>
                                    )
                                  })()
                                : null}
                            </Fragment>
                          ))
                        : null}
                      {/* System milk renders once at MEALTIME level (the Milk
                          disclosure after the item rows), never per bundle —
                          see PROD-1214. */}
                      {/* Undo every typed component number on the row — the
                          cells go back to tracking the meal counts. */}
                      {meta?.expanded && split && !readOnly && split.hasTyped(it.id) ? (
                        <tr className="border-b bg-muted/30 last:border-b-0">
                          <td colSpan={columns.length + 1} className="py-2 pl-[62px] pr-4">
                            <button
                              type="button"
                              onClick={() => split.onResetRow(it)}
                              className="rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 underline decoration-muted-foreground/60 decoration-dashed underline-offset-4 hover:bg-muted hover:text-foreground">
                              Reset components
                            </button>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </Fragment>
            ))}
            {/* Milk is part of the meal — one mealtime-level disclosure for
                every campus (PROD-1214); the dropdown is where a school
                changes counts of just milk manually. */}
            {standaloneMilk ? (
              <Fragment>
                <tr className="border-b last:border-b-0">
                  <td className="px-4 py-3 align-top">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                        <Milk className="h-4 w-4 text-muted-foreground" />
                      </span>
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-sm font-medium">Milk</span>
                        <span className="text-xs text-muted-foreground">
                          Included with each meal — auto-filled from the meal counts
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMilkExpanded((v) => !v)}
                      aria-expanded={milkExpanded}
                      title={milkSummary}
                      className="ml-[40px] mt-0.5 flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
                      <ChevronRight
                        className={cn('h-3 w-3 shrink-0 transition-transform', milkExpanded && 'rotate-90')}
                      />
                      <span className="truncate underline decoration-muted-foreground/60 decoration-dashed underline-offset-4">
                        {milkSummary}
                      </span>
                    </button>
                  </td>
                  {columns.map((col) => {
                    const total = milkColumnTotal(col.columnId)
                    return (
                      <td key={col.columnId} className="whitespace-nowrap px-4 py-3 align-top">
                        {total === null ? (
                          <span
                            title="Not available for this group"
                            className={cn(qtyCellShellClass(placedView), 'h-7 w-16 text-sm text-muted-foreground')}>
                            —
                          </span>
                        ) : placedView ? (
                          <PlacedQtyText value={total} className="h-7 w-16 text-sm" />
                        ) : (
                          // Read-only: the variety cells in the dropdown are the
                          // editable numbers and this total is their sum.
                          <span className="grid h-7 w-16 place-items-center text-sm tabular-nums text-muted-foreground">
                            {total}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
                {milkExpanded
                  ? standaloneMilk.rows.map((milkRow) => (
                      <tr key={`milk:${milkRow.id}`} className="border-b bg-muted/30 last:border-b-0">
                        <td className="py-2 pl-[62px] pr-4 align-middle">
                          <span className="text-xs text-muted-foreground">{milkRow.name}</span>
                        </td>
                        {columns.map((col) => {
                          const columnLocked = milkColumnLocked(col.columnId)
                          return (
                            <td key={col.columnId} className="whitespace-nowrap px-4 py-2 align-middle">
                              {standaloneMilk.isAvailable(milkRow.id, col.columnId) ? (
                                <QtyInput
                                  value={standaloneMilk.getQty(milkRow.id, col.columnId)}
                                  onChange={(n) => standaloneMilk.onSetQty(milkRow.id, col.columnId, n)}
                                  readOnly={readOnly || columnLocked}
                                  display={placedView}
                                  suggested={!columnLocked && standaloneMilk.isDefault(milkRow.id, col.columnId)}
                                  ariaLabel={qtyCellLabel(milkRow.name, col)}
                                  className="h-7 w-16 px-2 py-0 text-sm"
                                />
                              ) : (
                                <span
                                  className={cn(
                                    qtyCellShellClass(placedView),
                                    'h-7 w-16 text-sm text-muted-foreground',
                                  )}>
                                  {EMPTY_QTY_PLACEHOLDER}
                                </span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))
                  : null}
                {milkExpanded && showMilkReset ? (
                  <tr className="border-b bg-muted/30 last:border-b-0">
                    <td colSpan={columns.length + 1} className="py-2 pl-[62px] pr-4">
                      <button
                        type="button"
                        onClick={() => standaloneMilk.onReset?.()}
                        className="rounded-md px-1.5 py-0.5 text-xs font-medium text-foreground/80 underline decoration-muted-foreground/60 decoration-dashed underline-offset-4 hover:bg-muted hover:text-foreground">
                        Reset milk
                      </button>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ) : null}
            {(extraRows ?? []).map((row) => (
              <tr key={`extra-${row.menuItemId}`} className="border-b last:border-b-0">
                <td className="px-4 py-3 align-top">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                      {row.fieldTrip ? (
                        <Backpack className="h-4 w-4 text-muted-foreground" />
                      ) : row.systemMilk ? (
                        <Milk className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex items-center gap-1.5 truncate text-sm font-medium">
                        {row.name}
                        {row.fieldTrip ? (
                          <Badge variant="secondary" className="shrink-0 text-[10px] font-medium">
                            Field trip
                          </Badge>
                        ) : null}
                      </span>
                      <span className="text-xs text-muted-foreground">{extraRowCaption(row)}</span>
                    </div>
                  </div>
                </td>
                {columns.map((col) => (
                  <td key={col.columnId} className="whitespace-nowrap px-4 py-3 align-top">
                    <QtyInput
                      value={getQty(row.menuItemId, col.columnId)}
                      onChange={(n) => onSetExtraQty?.(row, col, n)}
                      readOnly={readOnly || !onSetExtraQty}
                      display={placedView}
                      ariaLabel={qtyCellLabel(row.name, col)}
                      className="h-7 w-16 px-2 py-0 text-sm"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MenuItemDetailModal
        menuItemId={detailItemId}
        serviceDate={serviceDate}
        // Price shows on click-in regardless of the inline toggle — schools
        // that keep prices off the planning grid can still look one up.
        priceLabel={detailItem ? itemPriceLabel(detailItem) : null}
        // Row-passed like priceLabel: a row reading "Condiments: …" must never
        // open a modal that omits them (PROD-136).
        condiments={detailItem?.bundle?.condiments ?? null}
        open={detailItemId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDetailItemId(null)
          }
        }}
      />
    </>
  )
}
